/* eslint-disable react/prop-types */
/* eslint-disable react/function-component-definition */

import { useEffect, useState } from "react";

// MUI Components
import { IconButton, Tooltip } from "@mui/material";

// Common Components
import Author from "components/Table/Author";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Constants
import Constants, { Colors, defaultData, Icons, PrecisionUtils } from "utils/Constants";

// 3rd party libraries
import moment from "moment";

export default function ReportCalculationData(
  data,
  reportData,
  totalCompletionPercentage,
  handleLocationClick
) {
  // Initial State
  const [rows, setRows] = useState([]);
  const [scopeHeader, setScopeHeader] = useState([]);
  const [reportTitleHeader, setReportTitleHeader] = useState([]);
  const [reportValueHeader, setReportValueHeader] = useState([]);
  const [reportCompletionFooter, setReportCompletionFooter] = useState([]);
  const [reportTotalFooter, setReportTotalFooter] = useState([]);

  const handleCellBackground = (value) =>
    PrecisionUtils.getCompletionBackgroundColor(value, Colors);

  const completionPercentage = (isRequired, completion) =>
    PrecisionUtils.formatCompletionPercentage(isRequired, completion);

  useEffect(() => {
    if (data) {
      const reportTitleList = [];
      const reportValueList = [];
      const reportCompletionList = [];
      let totalReportColSpan = 0;

      const scopeList = data.map((scope) => {
        scope?.reports?.forEach((report) => {
          reportTitleList.push({
            Header: `${report?.title}`,
            align: "center",
            colSpan: 1,
            key: report[Constants.MONGOOSE_ID],
          });
          reportValueList.push({
            Header: `${report?.weightage?.toFixed(2)}%`,
            align: "center",
            colSpan: 1,
            type: report?.type,
            key: report[Constants.MONGOOSE_ID],
            accessor: report[Constants.MONGOOSE_ID],
          });
          reportCompletionList.push({
            Footer: `${report?.reportCompletion?.toFixed(2) || 0}%`,
            align: "center",
            colSpan: 1,
            key: report[Constants.MONGOOSE_ID],
          });
        });
        totalReportColSpan += scope?.reports?.length || 0;
        return {
          Header: (
            <MDBox sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
              <MDBox
                sx={{
                  width: 16,
                  height: 16,
                  backgroundColor: scope?.colorCode || "#000",
                  borderRadius: "2px",
                  flexShrink: 0,
                }}
              />
              {scope?.name}
            </MDBox>
          ),
          align: "center",
          colSpan: scope?.reports?.length,
          key: scope[Constants.MONGOOSE_ID],
        };
      });

      // set the headers and footers
      setScopeHeader([
        { Header: "", colSpan: 2, key: "scope" },
        ...scopeList,
        { Header: "", colSpan: 1, key: "locationCompletionScope" },
      ]);
      setReportTitleHeader([
        { Header: "", colSpan: 2, key: "reportTitle" },
        ...reportTitleList,
        { Header: "", colSpan: 1, key: "locationCompletionTitle" },
      ]);
      setReportValueHeader([
        {
          Header: "Location",
          accessor: "location",
          align: "left",
          width: "16%",
          type: "location",
          key: "location",
        },
        {
          Header: "Asset",
          accessor: "asset",
          align: "left",
          width: "14%",
          type: "asset",
          key: "asset",
        },
        ...reportValueList,
        {
          Header: "Location Completion",
          accessor: "locationCompletion",
          align: "center",
          width: "12%",
          type: "locationCompletion",
          key: "locationCompletion",
        },
      ]);
      setReportCompletionFooter([
        { Footer: "Completion", align: "center", colSpan: 2, key: "completion" },
        ...reportCompletionList,
        { Footer: "", align: "center", colSpan: 1, key: "locationCompletionBlank" },
      ]);
      setReportTotalFooter([
        {
          Footer: "",
          align: "center",
          colSpan: totalReportColSpan + 1,
          key: "completion",
        },
        { Footer: "Total Completion", align: "center", colSpan: 1, key: "totalCompletion" },
        {
          Footer: `${totalCompletionPercentage}%`,
          align: "center",
          colSpan: 1,
          key: "totalPercentage",
        },
      ]);
    }
    if (reportData) {
      const dataList = reportData?.map((report) => ({
        key: report[Constants.MONGOOSE_ID],
        rowSpan: report?.assets?.length || 1,
        colSpan: 1,
        align: "left",
        location: (
          <MDBox
            sx={{
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "max-content",
              maxWidth: "250px",
            }}
            onClick={() => {
              handleLocationClick(report[Constants.MONGOOSE_ID], report?.locations);
            }}
          >
            <Author
              name={report?.locations || ""}
              style={{
                textTransform: "none",
                whiteSpace: "normal",
                wordBreak: "break-word",
              }}
            />
            <IconButton
              className="eye-icon-button"
              aria-label="report-parameter"
              color="info"
              size="small"
              sx={{ flexShrink: 0, padding: 0.25 }}
            >
              {Icons.VIEW}
            </IconButton>
          </MDBox>
        ),
        assets: report?.assets?.map((asset) => {
          const assetTitle = asset?.title || "";
          const fromTo = `(${asset?.fromLocation?.title || ""} - ${
            asset?.toLocation?.title || ""
          })`;
          const fullLabel = `${assetTitle} ${fromTo}`;
          return {
            title: (
              <Tooltip title={fullLabel} arrow placement="right">
                <MDBox
                  sx={{
                    display: "flex",
                    alignItems: "baseline",
                    maxWidth: "190px",
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                    lineHeight: 1.2,
                  }}
                >
                  <MDTypography
                    component="span"
                    variant="caption"
                    sx={{
                      fontSize: "14px",
                      textTransform: "none",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      flexShrink: 0,
                    }}
                  >
                    {assetTitle}
                  </MDTypography>
                  <MDTypography
                    component="span"
                    variant="caption"
                    sx={{
                      fontSize: "11px",
                      color: Colors.LIGHT_GRAY1,
                      textTransform: "none",
                      ml: 0.5,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {fromTo}
                  </MDTypography>
                </MDBox>
              </Tooltip>
            ),
            key: asset[Constants.MONGOOSE_ID],
          };
        }),
        reports: report?.reportList?.map((item) => ({
          key: item[Constants.MONGOOSE_ID],
          type: item?.type,
          rowSpan: item?.type === "location" ? report?.assets?.length || 1 : 1,
          colSpan: 1,
          align: "center",
          reportList: item?.reportList?.map((ele) => ({
            completion: (
              <MDBox sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
                <MDBox sx={{ width: "max-content" }}>
                  {!PrecisionUtils.isCompleted(ele?.completion) ? (
                    <Author
                      style={{ textTransform: "none", fontSize: "12px" }}
                      name={completionPercentage(ele?.isRequired, ele?.completion)}
                    />
                  ) : (
                    <Author
                      name={moment(ele?.completedDate).format(
                        ele.status.some((status) =>
                          [Constants.CHECKED, Constants.CLOSED].includes(status)
                        )
                          ? defaultData.WEB_DATE_FORMAT
                          : completionPercentage(ele?.isRequired, ele?.completion)
                      )}
                      style={{ textTransform: "none", fontSize: "12px" }}
                    />
                  )}
                </MDBox>
              </MDBox>
            ),
            isRequired: ele?.isRequired,
            bgColor: ele?.isRequired ? handleCellBackground(ele?.completion) : Colors.GREY1,
          })),
        })),
        locationCompletion: {
          value: (
            <span style={{ fontSize: "12px" }}>
              {`${PrecisionUtils.getCompletionDisplayValue(report?.locationCompletion)}%`}
            </span>
          ),
          bgColor: handleCellBackground(report?.locationCompletion),
          align: "center",
        },
      }));

      setRows(dataList);
    }
  }, [data, reportData, totalCompletionPercentage]);

  return {
    columns: reportValueHeader,
    extraHeadersList: [scopeHeader, reportTitleHeader],
    footerList: [reportCompletionFooter, reportTotalFooter],
    rows,
  };
}
